/**
 * Poisoned-transcript repair for the Claude Agent SDK driver.
 *
 * The Anthropic Messages API can permanently reject a conversation once its
 * persisted history contains a malformed content block. Because the bad block
 * lives in the SDK's on-disk JSONL transcript, every replayed turn fails with
 * the same `400 invalid_request_error` and the session is bricked with no
 * in-band recovery.
 *
 * This module repairs the transcript in place, covering three known poison
 * classes:
 *
 * 1. **Image `media_type` mismatch / omission.** An image block whose declared
 *    `media_type` does not match — or is missing for — the actual bytes:
 *    `... The image was specified using the image/png media type, but the
 *    image appears to be a image/jpeg image`. Produced by the Claude Code
 *    `Read` tool's format fallback (anthropics/claude-code#55338, #30124,
 *    #33179). Repaired by sniffing the base64 magic bytes and correcting
 *    `media_type`.
 * 2. **Oversized image.** An image whose decoded payload exceeds the API's
 *    5 MB per-image limit (anthropics/claude-code#34566). Replaced with a
 *    text stub — the byte budget cannot be recovered any other way.
 * 3. **`cache_control` on an empty text block.** A text block carrying a
 *    `cache_control` marker with empty/whitespace `text`
 *    (anthropics/claude-code#59626). The `cache_control` field is stripped.
 *
 * Genuinely unidentifiable image blocks are also replaced with a text stub.
 *
 * Recovering by content scan (rather than parsing the API's `messages.N`
 * index) is deliberate: the index addresses the assembled API request, not a
 * JSONL line, and reproducing the SDK's message-assembly is fragile. A full
 * scan is index-free and repairs every poisoned block in one pass.
 *
 * **Async and line-streaming since #454.** A Claude Code transcript is routinely
 * multi-megabyte (base64 images inline in `tool_result` blocks), and this runs on
 * a failed turn *and before every resume* — inside the single-threaded `skaile
 * serve` loop. The previous `readFileSync` + `writeFileSync` + `renameSync` held
 * the loop for the whole file and materialised it twice over as one string. Both
 * passes now stream line by line off a `createReadStream`, so the loop is yielded
 * between lines and peak memory is one JSONL line rather than the whole file, and
 * every I/O phase is deadline-bounded.
 *
 * Streaming was chosen over a worker thread: the work is I/O-bound rather than
 * CPU-bound (per-line `JSON.parse` on a yielded loop is not what stalls a turn),
 * and a worker would add a structured-clone of the same multi-MB payload plus a
 * thread lifecycle to a path that must never block a turn.
 *
 * @module
 */

import { createReadStream, createWriteStream } from "node:fs";
import { readdir, rename, stat, unlink } from "node:fs/promises";
import { join } from "node:path";
import { pipeline } from "node:stream/promises";
import { fsDeadlineMs, tryWithFsDeadline, withFsDeadline } from "@skaile/workspaces/core";

/**
 * Minimal logger shape accepted by {@link scrubPoisonedTranscript}. Compatible
 * with the bridge `Logger`, but narrowed so the helper stays unit-testable
 * without a real logger instance.
 *
 * @category Transcript repair
 */
export interface ScrubLogger {
  info(message: string, data?: Record<string, unknown>): void;
  warn(message: string, data?: Record<string, unknown>): void;
}

/**
 * Options for {@link scrubPoisonedTranscript}.
 *
 * @category Transcript repair
 */
export interface ScrubTranscriptOptions {
  /**
   * Claude Code config directory — the parent of `projects/`. Resolve from
   * `CLAUDE_CONFIG_DIR`, falling back to `~/.claude`.
   */
  configDir: string;
  /** SDK session id whose transcript JSONL should be repaired. */
  sessionId: string;
  /** Optional logger for diagnostics. */
  log?: ScrubLogger;
}

/**
 * Outcome of a {@link scrubPoisonedTranscript} pass.
 *
 * @category Transcript repair
 */
export interface ScrubTranscriptResult {
  /** The located JSONL transcript path, or `null` when not found. */
  filePath: string | null;
  /** Image blocks whose `media_type` was corrected (or filled in) to match the bytes. */
  corrected: number;
  /**
   * Image blocks replaced with a text stub — either unidentifiable bytes or a
   * payload over the API's 5 MB per-image limit.
   */
  stubbed: number;
  /** Empty text blocks from which a rejected `cache_control` marker was stripped. */
  cacheStripped: number;
  /**
   * `true` when the transcript was located, contained at least one poisoned
   * block, and was rewritten. `false` means nothing needed repair (or the
   * file was not found) — the caller should not retry the resume.
   */
  changed: boolean;
}

/** Text inserted in place of an image block whose format cannot be identified. */
const STUB_TEXT = "[image removed: unprocessable image data]";

/** Text inserted in place of an image block that exceeds the API size limit. */
const STUB_TEXT_OVERSIZED = "[image removed: image too large]";

/**
 * The Anthropic Messages API rejects any single image whose decoded payload
 * exceeds 5 MB. An image at or under this is left alone; a larger one is
 * stubbed (its byte budget cannot be recovered).
 */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/**
 * Identify an image's media type from the leading bytes of its base64 payload.
 *
 * Recognises the four formats the Anthropic API accepts. Returns `null` when
 * the magic bytes match no known format — the caller treats that as a
 * genuinely corrupt block and stubs it.
 *
 * @param base64 - Base64-encoded image data (only the prefix is inspected).
 * @returns An `image/*` media type, or `null` when unrecognised.
 * @category Transcript repair
 */
export function sniffImageMediaType(base64: string): string | null {
  let buf: Buffer;
  try {
    // 32 base64 chars decode to 24 bytes — enough for the WebP check at 12.
    buf = Buffer.from(base64.slice(0, 32), "base64");
  } catch {
    return null;
  }
  if (buf.length < 4) {
    return null;
  }
  // JPEG — SOI marker `FF D8`. Third byte varies (and may be corrupt), so a
  // two-byte check is intentional: it still matches malformed JPEGs, which is
  // exactly the poison case the API flags as "appears to be image/jpeg".
  if (buf[0] === 0xff && buf[1] === 0xd8) {
    return "image/jpeg";
  }
  // PNG — `89 50 4E 47`.
  if (buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4e && buf[3] === 0x47) {
    return "image/png";
  }
  // GIF — `47 49 46 38` ("GIF8").
  if (buf[0] === 0x47 && buf[1] === 0x49 && buf[2] === 0x46 && buf[3] === 0x38) {
    return "image/gif";
  }
  // WebP — `RIFF` at 0..3 and `WEBP` at 8..11.
  if (
    buf.length >= 12 &&
    buf[0] === 0x52 &&
    buf[1] === 0x49 &&
    buf[2] === 0x46 &&
    buf[3] === 0x46 &&
    buf[8] === 0x57 &&
    buf[9] === 0x45 &&
    buf[10] === 0x42 &&
    buf[11] === 0x50
  ) {
    return "image/webp";
  }
  return null;
}

/** A base64 image source on a content block (shape is SDK-defined). */
interface Base64ImageSource {
  type?: string;
  media_type?: string;
  data?: string;
}

/** Loosely-typed content block from a transcript message. */
interface ContentBlockLike {
  type?: string;
  source?: Base64ImageSource;
  content?: unknown;
  [k: string]: unknown;
}

/** Mutable counters threaded through one scrub pass. */
interface ScrubCounters {
  corrected: number;
  stubbed: number;
  cacheStripped: number;
}

/** Total repairs recorded in a counter set — used to detect per-line changes. */
function totalChanges(c: ScrubCounters): number {
  return c.corrected + c.stubbed + c.cacheStripped;
}

/**
 * Repair a single content block. Returns the (possibly replaced) block.
 *
 * - A `tool_result` block recurses into its `content` array.
 * - A text block carrying a `cache_control` marker whose `text` is empty or
 *   whitespace has the marker stripped (the API rejects `cache_control` on
 *   empty text blocks).
 * - An image block whose decoded payload exceeds the API's 5 MB limit is
 *   replaced with a text stub.
 * - An image block whose bytes match no known format is replaced with a text
 *   stub (an image cannot simply be dropped — a `tool_result` must keep at
 *   least one content entry, and a bare `image` block is replaced 1:1).
 * - An image block whose declared `media_type` mismatches (or is missing for)
 *   the sniffed type is corrected in place.
 * - Every other block is returned untouched.
 */
function scrubBlock(block: ContentBlockLike, counters: ScrubCounters): ContentBlockLike {
  if (block.type === "tool_result" && Array.isArray(block.content)) {
    return {
      ...block,
      content: (block.content as ContentBlockLike[]).map((inner) => scrubBlock(inner, counters)),
    };
  }

  // A `cache_control` marker on a text block whose text is empty or whitespace
  // is rejected by the API ("cache_control cannot be set for empty text
  // blocks"). Strip the marker — the empty block itself is harmless.
  if (block.type === "text" && block.cache_control != null) {
    const text = typeof block.text === "string" ? block.text : "";
    if (text.trim() === "") {
      counters.cacheStripped++;
      const clone = { ...block };
      delete clone.cache_control;
      return clone;
    }
  }

  if (
    block.type === "image" &&
    block.source?.type === "base64" &&
    typeof block.source.data === "string"
  ) {
    const data = block.source.data;
    // The API rejects any image whose decoded payload exceeds 5 MB. The byte
    // budget cannot be salvaged, so the whole block becomes a text stub.
    // base64 encodes 3 bytes per 4 chars, so decoded length ≈ len * 3 / 4.
    if (Math.floor((data.length * 3) / 4) > MAX_IMAGE_BYTES) {
      counters.stubbed++;
      return { type: "text", text: STUB_TEXT_OVERSIZED };
    }
    const sniffed = sniffImageMediaType(data);
    if (sniffed === null) {
      counters.stubbed++;
      return { type: "text", text: STUB_TEXT };
    }
    if (sniffed !== block.source.media_type) {
      counters.corrected++;
      return { ...block, source: { ...block.source, media_type: sniffed } };
    }
  }

  return block;
}

/**
 * Locate the JSONL transcript for `sessionId` under `<configDir>/projects/`.
 *
 * Claude Code encodes the project directory into the `projects/` subfolder
 * name; rather than reproduce that encoding, this scans every subfolder for
 * `<sessionId>.jsonl` (the session id is a UUID — globally unique).
 *
 * Async and deadline-bounded since #454. The `existsSync(projectsDir)` probe is
 * gone: `readdir` already fails on a missing directory, so the probe was a
 * second stat on the same path for no information.
 *
 * `timeoutMs` defaults to {@link fsDeadlineMs} so callers that only need the
 * path (the resume probe) can omit it.
 *
 * @category Transcript repair
 */
export async function locateTranscriptPath(
  configDir: string,
  sessionId: string,
  timeoutMs: number = fsDeadlineMs(),
): Promise<string | null> {
  const projectsDir = join(configDir, "projects");
  const entries = await tryWithFsDeadline(
    `readdir ${projectsDir}`,
    () => readdir(projectsDir),
    null,
    timeoutMs,
  );
  if (!entries) return null;
  for (const entry of entries) {
    const candidate = join(projectsDir, entry, `${sessionId}.jsonl`);
    const found = await tryWithFsDeadline(
      `stat ${candidate}`,
      async () => (await stat(candidate)).isFile(),
      false,
      timeoutMs,
    );
    if (found) return candidate;
  }
  return null;
}

/**
 * Yield a file's contents line by line, reproducing `readFileSync(...).split("\n")`
 * exactly — including the final (possibly empty) segment, so a trailing newline
 * survives a rewrite byte for byte.
 *
 * Splits on `\n` alone, so a line out of a CRLF transcript arrives with its
 * `\r` still attached. That carrier byte is part of the line here, and
 * {@link repairLine} re-attaches it to a line it rewrites — the file keeps the
 * terminator style it arrived with. Deliberately not `node:readline`:
 * `crlfDelay` strips the `\r` outright (rewriting a CRLF transcript to LF) and
 * the interface swallows the trailing-newline distinction, either of which
 * would make the "clean transcript is left byte-for-byte untouched" guarantee
 * false. Only one line is held in memory at a time, so a multi-MB transcript
 * never materialises as a single string.
 */
async function* streamLines(filePath: string): AsyncGenerator<string> {
  const stream = createReadStream(filePath, { encoding: "utf8" });
  let pending = "";
  try {
    for await (const chunk of stream) {
      pending += chunk as string;
      let idx = pending.indexOf("\n");
      while (idx !== -1) {
        yield pending.slice(0, idx);
        pending = pending.slice(idx + 1);
        idx = pending.indexOf("\n");
      }
    }
    yield pending;
  } finally {
    stream.destroy();
  }
}

/**
 * Repair one JSONL line, returning the (possibly rewritten) line and mutating
 * `counters`. A non-JSON, blank, or content-less line is returned untouched.
 *
 * The reader splits on `\n` only, so a line from a CRLF transcript still carries
 * its trailing `\r`. An untouched line is returned verbatim and keeps it; a
 * rewritten one is rebuilt by `JSON.stringify`, which would drop it — leaving a
 * repaired CRLF transcript with mixed line endings. So the terminator is split
 * off before the parse and re-appended to the rewritten line.
 */
function repairLine(line: string, counters: ScrubCounters): string {
  if (line.trim() === "") return line;
  const eol = line.endsWith("\r") ? "\r" : "";
  const body = eol === "" ? line : line.slice(0, -1);
  let entry: { message?: { content?: unknown } };
  try {
    entry = JSON.parse(body);
  } catch {
    return line; // Not JSON — leave untouched.
  }
  const content = entry.message?.content;
  if (!Array.isArray(content)) return line;
  const before = totalChanges(counters);
  const scrubbed = (content as ContentBlockLike[]).map((block) => scrubBlock(block, counters));
  if (totalChanges(counters) === before) return line; // Nothing changed on this line.
  return `${JSON.stringify({ ...entry, message: { ...entry.message, content: scrubbed } })}${eol}`;
}

/**
 * Scan a Claude Code SDK transcript for image content blocks whose declared
 * `media_type` does not match the actual bytes, repair them, and atomically
 * rewrite the file.
 *
 * Safe to call after a turn has failed: the SDK query is dead at that point,
 * and the next resume re-reads the transcript from disk.
 *
 * **Async since #454.** Two streaming passes rather than one whole-file read:
 * pass 1 counts repairs without writing anything, and pass 2 runs only when
 * pass 1 found something. The common case — the preventive scrub of a clean
 * transcript before every resume — is therefore one streaming read and zero
 * writes, and a clean transcript is still left byte-for-byte untouched.
 *
 * @param opts - Config directory, session id, and an optional logger.
 * @returns A {@link ScrubTranscriptResult}. When `changed` is `true` the
 *   caller may retry the resume with the same session id.
 * @category Transcript repair
 */
export async function scrubPoisonedTranscript(
  opts: ScrubTranscriptOptions,
): Promise<ScrubTranscriptResult> {
  const { configDir, sessionId, log } = opts;
  const timeoutMs = fsDeadlineMs();
  const result: ScrubTranscriptResult = {
    filePath: null,
    corrected: 0,
    stubbed: 0,
    cacheStripped: 0,
    changed: false,
  };

  const filePath = await locateTranscriptPath(configDir, sessionId, timeoutMs);
  if (!filePath) {
    log?.warn("scrub-transcript: no transcript found", { configDir, sessionId });
    return result;
  }
  result.filePath = filePath;

  // ── Pass 1: detect. Streams the transcript, counting repairs, writing nothing.
  const counters: ScrubCounters = { corrected: 0, stubbed: 0, cacheStripped: 0 };
  try {
    await withFsDeadline(
      `scan transcript ${filePath}`,
      async () => {
        for await (const line of streamLines(filePath)) repairLine(line, counters);
      },
      timeoutMs,
    );
  } catch (err) {
    log?.warn("scrub-transcript: failed to read transcript", {
      filePath,
      error: err instanceof Error ? err.message : String(err),
    });
    return result;
  }

  result.corrected = counters.corrected;
  result.stubbed = counters.stubbed;
  result.cacheStripped = counters.cacheStripped;

  if (totalChanges(counters) === 0) {
    return result;
  }

  // ── Pass 2: rewrite. Atomic: stream a sibling temp file, then rename over the
  // original. Line separators are re-emitted exactly as pass 1 split them, so a
  // trailing newline and any blank lines survive verbatim.
  const tmpPath = `${filePath}.scrub-tmp`;
  try {
    await withFsDeadline(
      `rewrite transcript ${filePath}`,
      async () => {
        const discard: ScrubCounters = { corrected: 0, stubbed: 0, cacheStripped: 0 };
        await pipeline(
          (async function* () {
            let first = true;
            for await (const line of streamLines(filePath)) {
              yield first ? repairLine(line, discard) : `\n${repairLine(line, discard)}`;
              first = false;
            }
          })(),
          createWriteStream(tmpPath, { encoding: "utf8" }),
        );
        await rename(tmpPath, filePath);
      },
      timeoutMs,
    );
  } catch (err) {
    log?.warn("scrub-transcript: failed to rewrite transcript", {
      filePath,
      error: err instanceof Error ? err.message : String(err),
    });
    // Never leave a half-written sibling behind — the next scrub would rewrite
    // it anyway, but a stray `.scrub-tmp` in the SDK's projects dir is noise.
    await tryWithFsDeadline(`unlink ${tmpPath}`, () => unlink(tmpPath), undefined, timeoutMs);
    return result;
  }

  result.changed = true;
  log?.info("scrub-transcript: repaired poisoned transcript", {
    filePath,
    corrected: result.corrected,
    stubbed: result.stubbed,
    cacheStripped: result.cacheStripped,
  });
  return result;
}
